import React, {useRef, useState} from 'react';
import {FlatList, Platform, Pressable, StyleSheet, View} from 'react-native';
import {Observer, observer} from 'mobx-react-lite';

import LoadingImg from '../../components/LoadingImg';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {
  ScreenTypes,
  useWebScreenDimensions,
} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import LastMinuteOptionTileCard from '../cart/components/LastMinuteOptionTileCard/LastMinuteOptionTileCardLoyaltyEnabled';

const WishlistScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.WISHLIST>) => {
    const {wishlistStore, userStore} = useStores();
    const listRef = useRef();
    const [showScrollButton, setShowScrollButton] = useState(false);
    const {isWebDesktopScreen, screenType} = useWebScreenDimensions();

    let numColumns = 2;

    switch (screenType) {
      case ScreenTypes.mobile:
        numColumns = 2;
        break;
      case ScreenTypes.desktop:
        numColumns = 4;
        break;
      case ScreenTypes.desktopLarge:
        numColumns = 6;
        break;
    }

    const productTileAbbreviatedHorizontalWrapperWidthStyle = StyleSheet.create(
      {
        main: {
          flex: 1 / numColumns,
        },
      },
    );

    if (!userStore.sessionToken) {
      return (
        <ContentView
          testID="discover-screen"
          style={{justifyContent: 'center'}}>
          <Pressable
            onPress={() => navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN)}
            style={[styles.centerNotice, {marginTop: null}]}>
            <Text style={styles.largeNotice}>Please log in</Text>
            <Text style={styles.largeNotice}>to use wishlists</Text>
          </Pressable>
          {/* {!!isWebDesktopScreen && <WebFooter />} */}
        </ContentView>
      );
    } else {
      // Need to convert the observable to a plain data object because _getItem in flatlist doesn't
      // respect array boundaries when numcolumns > 1 and mobx will throw out of bounds errors
      return (
        // The "ProductTileAbbreviatedHorizontal"s have an extra 8 padding
        <ContentView testID="discover-screen">
          <FlatList
            testID="wishlist-screen-flatlist"
            key={`wishlist-flatlist-${numColumns}`}
            data={wishlistStore.wishlistProducts.slice()}
            renderItem={({item}) => (
              <View
                style={productTileAbbreviatedHorizontalWrapperWidthStyle.main}>
                <LastMinuteOptionTileCard item={item} fullWidth />
              </View>
            )}
            columnWrapperStyle={styles.columnWrapperStyle}
            numColumns={numColumns}
            showsVerticalScrollIndicator={isWebDesktopScreen}
            showsHorizontalScrollIndicator={isWebDesktopScreen}
            keyExtractor={(item, index) => `${item.id}-${index}`}
            ItemSeparatorComponent={() => (
              <View
                style={{marginTop: 8}}
                testID="wishlist-screen-flatlist-item-separator"
              />
            )}
            // Handle scroll to top button
            onScrollBeginDrag={() => setShowScrollButton(true)}
            onMomentumScrollBegin={() => setShowScrollButton(true)}
            onMomentumScrollEnd={() => setShowScrollButton(false)}
            onScrollEndDrag={() => setShowScrollButton(false)}
            ref={listRef}
            contentContainerStyle={styles.contentContainer}
            // Needs to use a loading flag and change depending on that state.
            ListEmptyComponent={() => (
              <Observer>
                {() =>
                  wishlistStore.loadingWishlistProducts ? (
                    <LoadingImg />
                  ) : (
                    <View style={styles.centerNotice}>
                      <Text
                        style={[
                          styles.largeNotice,
                          {
                            marginBottom: 16,
                          },
                        ]}>
                        NO PRODUCTS FOUND
                      </Text>
                      <Text
                        style={{
                          fontSize: 16,
                          fontWeight: '300',
                          color: theme.colour.text.heading.primary,
                          textAlign: 'center',
                          fontStyle: 'italic',
                        }}>
                        Some products in this wishlist may not be in stock at
                        this store.
                      </Text>
                    </View>
                  )
                }
              </Observer>
            )}
          />
          <ScrollToTopButton
            open={showScrollButton}
            callback={() =>
              listRef.current.scrollToIndex({index: 0, animated: true})
            }
          />
        </ContentView>
      );
    }
  },
);

export default WishlistScreen;

const styles = StyleSheet.create({
  columnWrapperStyle: {gap: 8},
  centerNotice: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginTop: '50%',
  },
  largeNotice: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  contentContainer: {
    ...Platform.select({
      ios: {},
      web: {
        flex: 1,
      },
    }),
  },
});
